import { Money, StruckSnapshot } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import { RateSource } from "@redotech/money/rate-source";
import { Rate } from "./rate";

export class RateMismatchError extends Error {
  override readonly name = "RateMismatchError";
  constructor(
    readonly moneyCurrency: Currency,
    readonly rateBase: Currency,
  ) {
    super(
      `Rate base ${rateBase} does not match money currency ${moneyCurrency}`,
    );
  }
}

/**
 * Convert into the rate's quote currency by multiplying by the rate.
 * Presentment-only — returns unstruck Money; use `strike` to freeze.
 */
export function applyRate(value: Money, rate: Rate): Money {
  if (value.currency !== rate.base) {
    throw new RateMismatchError(value.currency, rate.base);
  }
  if (rate.base === rate.quote) {
    return { amount: value.amount, currency: value.currency };
  }
  return { amount: value.amount * rate.rate, currency: rate.quote };
}

/**
 * Freeze `value`, recording `struckFrom` as a one-level snapshot (nested `struck`
 * dropped). Displays and refunds reconvert from `struck`, never a live rate;
 * provenance is audit/display metadata only.
 */
export function strike(
  value: Money,
  struckFrom: Money,
  provenance?: { source?: RateSource; asOf?: number },
): Money {
  const struck: StruckSnapshot = {
    amount: struckFrom.amount,
    currency: struckFrom.currency,
  };
  if (provenance?.source !== undefined) {
    struck.source = provenance.source;
  }
  if (provenance?.asOf !== undefined) {
    struck.asOf = provenance.asOf;
  }
  return { amount: value.amount, currency: value.currency, struck };
}

/** Convert then freeze: top-level amount is settled (quote), `struck` is presentment. */
export function convertAndStrike(value: Money, rate: Rate): Money {
  return strike(applyRate(value, rate), value, {
    source: rate.source,
    asOf: rate.asOf,
  });
}
